import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { toastHelper } from '../util/toastHelper.js'

const API_VERSION = 'v1'
const REQUEST_URL = `${process.env.REACT_APP_REQUEST_URL}/api/${API_VERSION}`

// Async thunk to fetch current user's friends info
export const getFriendsInfo = createAsyncThunk(
	'/api/friends/getFriendsInfo',
	async () => {
		try {
			const res = await fetch(`${REQUEST_URL}/friends/get-friends-info`, {
				method: 'GET',
				credentials: 'include',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
			})
			return await res.json()
		} catch (err) {
			console.log(err.message)
		}
	}
)

// Async thunk to search for new users to add as friends
export const findFriends = createAsyncThunk(
	'/api/friends/findFriends',
	async (data) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/friends/find-friends?searchQuery=${data.searchQuery}&page=${data.page}`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			return await res.json()
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const addFriendsByEmailBatch = createAsyncThunk(
	'friends/addFriendsByEmailBatch',
	async (rowsOrEmails) => {
		const emails = Array.from(
			new Set(
				(Array.isArray(rowsOrEmails) ? rowsOrEmails : [])
					.map((r) => (typeof r === 'string' ? r : r?.email))
					.filter(Boolean)
					.map((e) => String(e).trim().toLowerCase())
			)
		).slice(0, 10)

		if (!emails.length) return { results: [] }

		const results = []
		for (const email of emails) {
			try {
				const res = await fetch(`${REQUEST_URL}/friends/add-friend`, {
					method: 'PATCH',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ email }),
				})
				const data = await res.json().catch(() => ({}))
				results.push({ email, ok: res.ok && data?.success, data })
			} catch (err) {
				results.push({
					email,
					ok: false,
					data: { message: err.message || 'Request failed' },
				})
			}
		}

		return { results }
	}
)

// Async thunk to remove an existing friend by ID
export const removeFriend = createAsyncThunk(
	'/api/friends/removeFriend',
	async (friendId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/friends/remove-friend/${friendId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			return await res.json()
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const acceptFriendRequest = createAsyncThunk(
	'friend/acceptFriendRequest',
	async (friendId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/friends/accept-friend-request/${friendId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
				}
			)

			const data = await res.json()
			return data
		} catch (err) {
			console.error(err.message)
		}
	}
)

export const getAccountProfile = createAsyncThunk(
	'friend/getAccountProfile',
	async (userId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/accounts/get-account-profile/${userId}`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const updateVendorStaffProfile = createAsyncThunk(
	'friend/updateVendorStaffProfile',
	async (data) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/staff/update-vendor-staff-profile/${data.staffId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ ...data, staffId: undefined }),
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const addFriendChat = createAsyncThunk(
	'/api/friends/add-friend-chat',
	async (friendId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/friends/add-friend-chat/${friendId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			return await res.json()
		} catch (err) {
			console.log(err.message)
		}
	}
)

// Initial state for the friend slice
const initialState = {
	friends: [], // List of all friends
	friendSearchList: [], // Results from findFriends
	singleAccountProfile: null,
}

// Redux slice to manage friend-related state and actions
const friendSlice = createSlice({
	name: 'friend',
	initialState,
	reducers: {}, // No synchronous reducers needed
	extraReducers: (builder) => {
		builder
			// Handle success response from getFriendsInfo
			.addCase(getFriendsInfo.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					state.friends = []
					return
				}
				state.friends = payload.friends
			})

			// Handle success response from findFriends
			.addCase(findFriends.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					state.friendSearchList = []
					toastHelper(
						payload.message || 'Failed to find friends',
						'error'
					)
					return
				}
				state.friendSearchList = payload.members
			})

			.addCase(addFriendsByEmailBatch.fulfilled, (state, { payload }) => {
				for (const r of payload.results || []) {
					const { email, ok, data } = r
					if (!ok) {
						toastHelper(
							data?.message || `Failed: ${email}`,
							'error'
						)
						continue
					}
					const f = data?.friend
					if (f?._id) {
						const idx = state.friends.findIndex(
							(x) => x._id === f._id
						)
						if (idx >= 0)
							state.friends[idx] = { ...state.friends[idx], ...f }
						else state.friends.push(f)
					}
					toastHelper(
						data?.message || `Processed ${email}`,
						'success'
					)
				}
			})

			// Handle success response from removeFriend
			.addCase(removeFriend.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload.message || 'Failed to remove friend',
						'error'
					)
					return
				}
				toastHelper(
					payload.message || 'Friend successfully removed',
					'success'
				)
				state.friends = state.friends.filter(
					(friend) => friend._id !== payload.friendId
				)
			})

			.addCase(acceptFriendRequest.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to accept friend request',
						'error'
					)
					return
				}
				toastHelper(
					payload?.message || 'Friend request accepted',
					'success'
				)
				state.friends = state.friends.map((member) =>
					member._id === payload.friendId
						? { ...member, status: undefined }
						: member
				)
			})

			.addCase(getAccountProfile.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to get account profile',
						'error'
					)
					return
				}
				state.singleAccountProfile = payload.account
			})

			.addCase(
				updateVendorStaffProfile.fulfilled,
				(state, { payload }) => {
					if (!payload || !payload.success) {
						toastHelper(
							payload?.message ||
								'Failed to update staff profile',
							'error'
						)
						return
					}
					toastHelper(
						payload?.message ||
							'Successfully updated staff profile',
						'success'
					)
					state.singleAccountProfile = payload.updatedStaff
				}
			)

			.addCase(addFriendChat.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to add user chat',
						'error'
					)
					return
				}
			})
	},
})

export default friendSlice.reducer
